/**
 * Compact heat bar with engine-aligned warn / danger / shutdown
 * thresholds. The bar fills from 0 → HEAT_BAR_MAX; threshold ticks
 * render as small notches so the spectator can read "how close to
 * shutdown" without knowing the absolute numbers.
 */

import type { CSSProperties } from "react";
import {
  HEAT_BAR_MAX,
  HEAT_DANGER,
  HEAT_SHUTDOWN,
  HEAT_WARN,
  heatPhase,
  heatRatio,
  type HeatPhase,
} from "./derive";

export interface HeatBarProps {
  heat: number;
  /** Side accent — falls back to per-phase color. */
  accent?: string;
}

const trackStyle: CSSProperties = {
  position: "relative",
  width: "100%",
  height: 8,
  background: "var(--pp-bg)",
  border: "1px solid var(--pp-border)",
  borderRadius: "var(--pp-radius)",
  overflow: "hidden",
};

const fillStyle = (ratio: number, color: string): CSSProperties => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  width: `${ratio * 100}%`,
  background: color,
  transition: "width 120ms linear",
});

const tickStyle = (atRatio: number): CSSProperties => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: `${atRatio * 100}%`,
  width: 1,
  background: "var(--pp-border)",
  pointerEvents: "none",
});

const rootStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  width: "100%",
};

const labelRowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "var(--pp-font-sm)",
  color: "var(--pp-text-muted)",
};

const valueStyle: CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  color: "var(--pp-text)",
  fontWeight: "var(--pp-weight-bold)",
};

function colorForPhase(phase: HeatPhase, accent?: string): string {
  switch (phase) {
    case "safe":
      return accent ?? "var(--pp-ok)";
    case "warn":
      return "var(--pp-warn)";
    case "danger":
    case "critical":
      return "var(--pp-danger)";
  }
}

export function HeatBar({ heat, accent }: HeatBarProps) {
  const ratio = heatRatio(heat);
  const phase = heatPhase(heat);
  const color = colorForPhase(phase, accent);
  return (
    <div style={rootStyle} aria-label={`Heat ${heat} of ${HEAT_BAR_MAX}`}>
      <div style={labelRowStyle}>
        <span>HEAT</span>
        <span style={valueStyle}>{heat}</span>
      </div>
      <div style={trackStyle} role="progressbar" aria-valuemin={0} aria-valuemax={HEAT_BAR_MAX} aria-valuenow={heat}>
        <div style={fillStyle(ratio, color)} />
        <div style={tickStyle(HEAT_WARN / HEAT_BAR_MAX)} aria-hidden />
        <div style={tickStyle(HEAT_DANGER / HEAT_BAR_MAX)} aria-hidden />
        <div style={tickStyle(HEAT_SHUTDOWN / HEAT_BAR_MAX)} aria-hidden />
      </div>
    </div>
  );
}
